/**
 * Site Configuration Context
 * Loads public site branding, fonts, and theme config from /api/site-config.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface SiteFonts {
  primaryHeading: string;
  secondaryBody: string;
  accent: string;
  customCssUrl: string;
}

export interface SiteBrand {
  logoUrl: string;
  faviconUrl: string;
  appleTouchIconUrl: string;
  themeColor: string;
}

export interface SiteConfig {
  brand: SiteBrand;
  fonts: SiteFonts;
}

interface SiteConfigContextType {
  config: SiteConfig;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const DEFAULT_CONFIG: SiteConfig = {
  brand: {
    logoUrl: '',
    faviconUrl: '',
    appleTouchIconUrl: '',
    themeColor: '#1A2E12',
  },
  fonts: {
    primaryHeading: 'Oswald',
    secondaryBody: 'Jost',
    accent: 'EB Garamond',
    customCssUrl: '',
  },
};

function parseConfig(raw: Record<string, string>): SiteConfig {
  return {
    brand: {
      logoUrl: raw['brand.logo_url'] || '',
      faviconUrl: raw['brand.favicon_url'] || '',
      appleTouchIconUrl: raw['brand.apple_touch_icon_url'] || '',
      themeColor: raw['brand.theme_color'] || '#1A2E12',
    },
    fonts: {
      primaryHeading: raw['fonts.primary_heading'] || 'Oswald',
      secondaryBody: raw['fonts.secondary_body'] || 'Jost',
      accent: raw['fonts.accent'] || 'EB Garamond',
      customCssUrl: raw['fonts.custom_css_url'] || '',
    },
  };
}

const SiteConfigContext = createContext<SiteConfigContextType>({
  config: DEFAULT_CONFIG,
  isLoading: true,
  error: null,
  refresh: async () => {},
});

export function SiteConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/site-config`);
      if (!response.ok) throw new Error('Failed to load site config');
      const data = await response.json();
      if (data && typeof data.config === 'object') {
        setConfig(parseConfig(data.config));
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Keep defaults on error so the UI never breaks
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SiteConfigContext.Provider value={{ config, isLoading, error, refresh }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig(): SiteConfigContextType {
  return useContext(SiteConfigContext);
}

export function useSiteBrand(): SiteBrand {
  return useContext(SiteConfigContext).config.brand;
}

export function useSiteFonts(): SiteFonts {
  return useContext(SiteConfigContext).config.fonts;
}
